import React, { useState, useEffect } from 'react';
import { adminApi } from '../helpers/adminApi';
import UserList from './UserList';
import UserForm from './UserForm';

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadUsers();
    loadCurrentUser();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.fetchUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Erreur lors du chargement des utilisateurs');
      console.error('Load users error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentUser = async () => {
    try {
      const userData = await adminApi.getCurrentUser();
      setCurrentUser(userData);
    } catch (err) {
      console.error('Load current user error:', err);
    }
  };

  const handleCreate = async (formData) => {
    try {
      await adminApi.createUser(formData);
      await loadUsers();
      setShowForm(false);
    } catch (err) {
      throw new Error('Erreur lors de la création de l\'utilisateur');
    }
  };

  const handleUpdate = async (formData) => {
    try {
      await adminApi.updateUser(editingUser.id, formData);
      await loadUsers();
      setEditingUser(null);
      setShowForm(false);
    } catch (err) {
      throw new Error('Erreur lors de la mise à jour de l\'utilisateur');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      return;
    }

    try {
      await adminApi.deleteUser(userId);
      await loadUsers();
    } catch (err) {
      setError('Erreur lors de la suppression de l\'utilisateur');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingUser(null);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      user.username?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="d-flex justify-content-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        <button 
          className="btn btn-sm btn-outline-danger ms-2"
          onClick={loadUsers}
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div>
      {!showForm && (
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>
              Total: {filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''}
            </h5>
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              Ajouter un utilisateur
            </button>
          </div>
          
          <div className="row mb-3">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Rechercher par nom d'utilisateur ou email..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <UserForm
          user={editingUser}
          onSubmit={editingUser ? handleUpdate : handleCreate}
          onCancel={handleCancelForm}
        />
      )}

      {!showForm && (
        <UserList
          users={filteredUsers}
          currentUser={currentUser}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default UserManager;